import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPanel from '../panel/AdminPanel';
import '../styles/AdminDashboard.css';
import { FaRupeeSign, FaLink } from 'react-icons/fa';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "../../../api/api";

// Map icons (same as AdminPanel)
import dashboardIcon from '../icons/dashboard.png';
import logoutIcon from '../icons/logout.png';
import noticeIcon from '../icons/notice.png';
import notificationIcon from '../icons/notification.png';
import feesIcon from '../icons/fees.png';
import storeIcon from '../icons/store.png';
import transportIcon from '../icons/transport.png';
import hallticketIcon from '../icons/hallticket.png';
import userIcon from '../icons/user.svg';
import teacherIcon from '../icons/teacher.svg';
import bookIcon from '../icons/book.svg';
import timetableIcon from '../icons/timetable.png';
import studentIcon from '../icons/student.svg';

const iconMap = {
  'Admin Dashboard': dashboardIcon,
  'Manage User': userIcon,
  'Assign Teacher': teacherIcon,
  'Assign Students': studentIcon,
  'Class Timetable': timetableIcon,
  'Add Booklist': bookIcon,
  'Compose Notice': noticeIcon,
  'Compose Notification': notificationIcon,
  'Fee Structure': feesIcon,
  'Store Management': storeIcon,
  'Manage Transport': transportIcon,
  'Manage Hall Ticket': hallticketIcon,
};

// Add a color map for module tiles
const colorMap = {
  'Admin Dashboard': '#2196f3',
  'Manage User': '#4caf50',
  'Assign Teacher': '#ff9800',
  'Assign Students': '#ff9800',
  'Class Timetable': '#8e24aa',
  'Add Booklist': '#f44336',
  'Compose Notice': '#ef5350',
  'Compose Notification': '#f44336',
  'Fee Structure': '#e91e63',
  'Store Management': '#ff5722',
  'Manage Transport': '#03a9f4',
  'Manage Hall Ticket': '#8e24aa',
};

const AdminDashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [modules, setModules] = useState([]);
  const [schoolName, setSchoolName] = useState(''); // ✅ Added
  const navigate = useNavigate();
  const adminId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  const schoolId = localStorage.getItem('schoolId'); // ✅ Ensure this exists in localStorage

  // ✅ New useEffect to fetch school name
  useEffect(() => {
    const fetchSchool = async () => {
      try {
        const res = await api.get(`/api/schools/${schoolId}`, {
  headers: { Authorization: `Bearer ${token}` },
});
        setSchoolName(res.data.name);
      } catch (err) {
        console.error('Error fetching school:', err);
      }
    };

    if (schoolId && token) {
      fetchSchool();
    }
  }, [schoolId, token]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const schoolId = localStorage.getItem('schoolId');
        const res = await api.get(`/api/modules/${schoolId}`, {
  headers: { Authorization: `Bearer ${token}` },
});

        const enabledModules = res.data.filter(
  (m) => m.role === 'admin' && m.enabled
);
        setModules(enabledModules);
      } catch (err) {
        console.error('Error fetching modules:', err);
      }
    };
    fetchModules();
  }, [token]);

  const handleNavigate = (path) => {
    const finalPath = path.replace(':id', adminId);
    navigate(finalPath);
  };

  return (
    <>
      {/* Sidebar */}
      <AdminPanel onToggle={setIsSidebarCollapsed} />

      {/* Main content */}
      <div className={`admin-main-content ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="dashboard-topbar">
          <span className="date-time">TODAY IS {new Date().toLocaleString()}</span>

          {/* ✅ Replaced static school name with dynamic */}
          <span className="school-name">{schoolName || 'Loading...'}</span>
        </div>

        <h3 className="dashboard-title">
          <FaLink style={{ marginRight: "8px" }} />
          Quick Links
        </h3>

        <div className="quick-links">
          {modules.map((mod, index) => (
            <div
              key={`${mod.id || mod.name || 'module'}-${index}`}
              className="quick-link-card"
              onClick={() => handleNavigate(mod.path)}
              style={{ cursor: 'pointer', backgroundColor: colorMap[mod.name] || '#607d8b' }}
            >
              <div className="ql-left">
                <div className="ql-count">-</div>
                <div className="ql-label">{mod.name}</div>
              </div>
              <div className="ql-icon-chip">
                <img
                  src={iconMap[mod.name] || dashboardIcon}
                  alt={mod.name}
                  style={{ width: '24px', height: '24px' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* ACCOUNTS OVERVIEW CHART */}
        <div className="accounts-overview-chart">
          <h3 className="chart-title">
            <FaRupeeSign /> Accounts Overview
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={[
                  { name: "Gross Receivable", value: 494900 },
                  { name: "Collection", value: 205000 },
                  { name: "Collection Due", value: 289900 },
                ]}
                cx="50%"
                cy="50%"
                innerRadius={90}
                outerRadius={150}
                paddingAngle={5}
                dataKey="value"
                label={({ name, value }) => `${name}: ₹${value.toLocaleString()}`}
              >
                <Cell fill="#03a9f4" />
                <Cell fill="#4caf50" />
                <Cell fill="#f44336" />
              </Pie>
              <Tooltip formatter={(value) => `₹ ${value.toLocaleString()}`} />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* TABLE SECTION */}
        <div className="dashboard-table">
          <h3>Today's Enquiry Follow-up</h3>
          <table>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Student Name</th>
                <th>Mobile No</th>
                <th>Program</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>Aarav Sharma</td>
                <td>9876543210</td>
                <td>Science Stream (XI)</td>
              </tr>
              <tr>
                <td>2</td>
                <td>Priya Mehta</td>
                <td>9123456780</td>
                <td>Commerce Stream (XI)</td>
              </tr>
              <tr>
                <td>3</td>
                <td>Rohan Verma</td>
                <td>9988776655</td>
                <td>NEET Coaching</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ✅ New Hall Ticket Section */}
        <div className="dashboard-table">
          <h3>Hall Tickets Raised by Parents</h3>
          <table>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Student Name</th>
                <th>Roll No</th>
                <th>Exam Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>Ishita Kapoor</td>
                <td>2025-001</td>
                <td>12 Sept 2025</td>
                <td>Pending</td>
              </tr>
              <tr>
                <td>2</td>
                <td>Rahul Nair</td>
                <td>2025-002</td>
                <td>13 Sept 2025</td>
                <td>Approved</td>
              </tr>
              <tr>
                <td>3</td>
                <td>Sanya Gupta</td>
                <td>2025-003</td>
                <td>14 Sept 2025</td>
                <td>Rejected</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
